/**
 * Home page content. Copy supplied by the content writer; do not reword without approval.
 * SEO owner: title, description and H1 live here (see docs/SEO_GUIDELINES.md).
 */

export const HOME_SEO = {
  title: "A Learning Lab India | Beyond the classroom. Into the real world.",
  description:
    "Experiential learning for curious minds aged 13 to 18. Internships, bootcamps, masterclasses and industry immersion across 16 industries, with real mentors who do the job.",
  path: "/",
};

export const HERO = {
  h1Lead: "Beyond the classroom. Into the",
  h1Accent: "real world.",
  sub: "Experiential learning for curious minds of ages 13 to 18.",
  cta: { label: "Start exploring", to: "#experiences" },
  /**
   * Hero carousel: one teen + mentor cut-out per field, shown one at a time.
   * Images live in /public/hero (WebP, 532x1024); `file` is joined with PUBLIC_URL in the component.
   * `icon` is a key into HERO_ICONS in HeroCarousel.jsx.
   */
  slides: [
    { id: "engineering", file: "hero/engineering.webp", icon: "engineering", field: "Engineering", line: "Build it like an engineer.", alt: "A teen in a hard hat working on a robotics bench with an engineering mentor pointing at the equipment" },
    { id: "aviation", file: "hero/aviation.webp", icon: "aviation", field: "Aviation", line: "Fly it like a pilot.", alt: "A teen in a pilot uniform at a flight simulator with an aviation mentor" },
    { id: "healthcare", file: "hero/healthcare.webp", icon: "healthcare", field: "Healthcare", line: "Shadow a real doctor.", alt: "A teen in a lab coat using a stethoscope on a medical training mannequin while a healthcare instructor guides them" },
    { id: "law", file: "hero/education.webp", icon: "law", field: "Law & Policy", line: "Make your case like a lawyer.", alt: "A teen in a blazer reviewing documents at a table with a mentor in professional attire" },
    { id: "technology", file: "hero/technology.webp", icon: "technology", field: "Technology", line: "Build what's next.", alt: "A teen in a suit reviewing charts and graphs at a table with a mentor" },
  ],
};

/** `to` is the number to count up to; `static` values are shown as is. */
export const HERO_STATS = [
  { id: "ages", static: "13–18", label: "ages, built for teens" },
  { id: "careers", to: 120, suffix: "+", label: "careers to peek into" },
  { id: "days", static: "365", label: "days of things to do" },
  { id: "infinite", static: "∞", label: "infinite possibilities" },
];

export const CURIOSITY = {
  title: "Start with curiosity. See where it takes you.",
  intro:
    "The best learning happens when you build something, crack a hard question, walk into a new place, or learn from someone who actually does the job. That's A Learning Lab.",
  steps: [
    { id: "explore", title: "Explore", text: "Find interests school hasn't got time for." },
    { id: "experience", title: "Experience", text: "Step into real-world challenges." },
    { id: "create", title: "Create", text: "Turn ideas into things you can show." },
    { id: "connect", title: "Connect", text: "Meet mentors and people who get you." },
    { id: "grow", title: "Grow", text: "Gain skills, confidence, perspective." },
  ],
};

export const SUBJECT_PICKER = {
  title: "Your future isn't a mystery. It's a choice waiting to be made.",
  intro: "Most students choose subjects before knowing where they could take them. Try this: pick a subject you like.",
  emptyHint: "Tap a subject above",
  legend: "The highlighted one is the career most students have never heard of.",
  factSuffix: "opens far more doors than the obvious ones.",
  quote: {
    lead: "Grades show what you know.",
    main: "Experiences show",
    accent: "who you are.",
    cta: { label: "Find your experience", to: "#experiences" },
    hint: "Pick one that sounds like you.",
    chips: ["Shadow a pro", "Build something real", "Meet your mentor"],
  },
  // `surprise: true` marks the career most students have never heard of.
  subjects: [
    {
      id: "maths",
      name: "Maths",
      careers: [
        { name: "Actuary", surprise: true },
        { name: "Game developer" },
        { name: "Data scientist" },
        { name: "Venture capitalist" },
      ],
    },
    {
      id: "biology",
      name: "Biology",
      careers: [
        { name: "Veterinarian" },
        { name: "Sports scientist", surprise: true },
        { name: "Surgeon" },
        { name: "Climate tech researcher" },
      ],
    },
    {
      id: "physics",
      name: "Physics",
      careers: [
        { name: "Pilot" },
        { name: "Air traffic controller", surprise: true },
        { name: "Aerospace engineer" },
        { name: "Chip designer" },
      ],
    },
    {
      id: "chemistry",
      name: "Chemistry",
      careers: [
        { name: "Perfumer", surprise: true },
        { name: "Food scientist" },
        { name: "Pharmacist" },
        { name: "Sustainability consultant" },
      ],
    },
    {
      id: "art",
      name: "Art",
      careers: [
        { name: "Architect" },
        { name: "UI/UX designer" },
        { name: "Fashion designer" },
        { name: "Game designer", surprise: true },
      ],
    },
    {
      id: "english",
      name: "English",
      careers: [
        { name: "Journalist" },
        { name: "Brand strategist" },
        { name: "Lawyer" },
        { name: "Policy advisor", surprise: true },
      ],
    },
    {
      id: "economics",
      name: "Economics",
      careers: [
        { name: "Supply chain analyst", surprise: true },
        { name: "Management consultant" },
        { name: "Entrepreneur" },
        { name: "FinTech product manager" },
      ],
    },
  ],
};

export const SEASONS = {
  title: "Something to explore, all year round.",
  intro: "There's no off season for curiosity. Pick your moment.",
  cta: { label: "Explore what's coming up", to: "/consultation" },
  items: [
    { id: "weekends", name: "Weekends", line: "A weekend to explore one big idea." },
    { id: "breaks", name: "School breaks", line: "Skip the scroll. Build something instead." },
    { id: "summers", name: "Summers", line: "Long days. Bigger projects." },
    { id: "winters", name: "Winters", line: "Cold outside. Hot ideas." },
    { id: "year", name: "Throughout the year", line: "No season required. Start whenever you're ready." },
  ],
};

export const EXPERIENCES = {
  title: "Pick an experience. Take your interest further.",
  intro: "What do you feel like doing?",
  filters: [
    { id: "all", label: "Explore it all" },
    { id: "try", label: "Try a career" },
    { id: "skill", label: "Learn a skill" },
    { id: "build", label: "Build something" },
  ],
  items: [
    {
      id: "conferences",
      title: "Conferences",
      text: "Get into the room. Hear big ideas, take on challenges with other students.",
      takeaway: "A capstone project",
      tags: ["build"],
    },
    {
      id: "masterclasses",
      title: "Masterclasses",
      text: "Learn straight from people who've done it.",
      takeaway: "Advice you can't Google",
      tags: ["skill"],
    },
    {
      id: "internships",
      title: "Internships",
      text: "See how industries actually work from the inside.",
      takeaway: "Hands-on experience",
      tags: ["try"],
    },
    {
      id: "skill-development",
      title: "Skill development",
      text: "Practical skills school doesn't always have time for.",
      takeaway: "Skills that stick",
      tags: ["skill"],
    },
    {
      id: "observerships",
      title: "Medical observerships",
      text: "Get close to the reality of healthcare, with guidance.",
      takeaway: "A true picture of the job",
      tags: ["try"],
    },
    {
      id: "research",
      title: "Research and innovation",
      text: "Ask better questions. Turn your ideas into something you can build.",
      takeaway: "Your own project",
      tags: ["build"],
    },
  ],
  more: {
    title: "And more",
    text: "Your interests won't fit in a dropdown menu. Tell us what you're into and we'll point you somewhere good.",
    cta: { label: "Tell us what you're into", to: "/consultation" },
  },
};

export const MENTORS = {
  title: "The mentors who make learning come alive",
  intro:
    "Meet experts from different backgrounds, industries and paths. Ask the questions you're curious about, get honest answers, and see what their experiences can teach you.",
  questions: [
    "What did you study, and would you pick it again?",
    "What's the hardest part of your job nobody talks about?",
    "If you were 13 again, what would you do differently?",
  ],
  carouselTitle: "Our mentor network",
  // Mentor profiles come from https://alearninglab.com/conferences/ (mentors from prior cohorts).
  // Replace with the backend / CMS feed when it exists.
  sample: false,
  footnote: "The profiles shown reflect mentors from prior cohorts.",
  people: [
    { id: "charlie-meyer", name: "Charlie Meyer", role: "Economics", org: "Harvard University", note: "Sophomore concentrating in Economics with a secondary in Energy & Environment.", image: "https://alearninglab.com/wp-content/uploads/2025/04/Charlie-mentor-economics.jpeg" },
    { id: "ty-fein", name: "Ty Fein", role: "Economics", org: "Harvard University", note: "Economics student involved in student agencies and Varsity Football.", image: "https://alearninglab.com/wp-content/uploads/2025/04/Fy-Economics.jpeg" },
    { id: "christopher", name: "Christopher", role: "Economics & Computer Science", org: "Harvard University", note: "Senior with leadership, teaching, and student organization experience.", image: "https://alearninglab.com/wp-content/uploads/2025/04/Christopher-mentor.jpeg" },
    { id: "kostadin-devedzhiev", name: "Kostadin Devedzhiev", role: "AI Researcher", org: "University of Cambridge", note: "Advancing human-inspired AI, multi-agent systems, and human–machine collaboration.", image: "https://d23ed2vwswjjj7.cloudfront.net/all/2025/12/headshot-Kostadin-Devedzhiev-1.png" },
    { id: "dana-yeleussiz", name: "Dana Yeleussiz", role: "Former McKinsey Strategist", org: "AI & Education", note: "Building AI solutions to reimagine education, mentorship, and global access.", image: "https://d23ed2vwswjjj7.cloudfront.net/all/2025/12/DSCF2023-Dana-Yeleussiz-1.png" },
    { id: "chan-leem", name: "Chan Leem", role: "International Policy Specialist", org: "Cyber Governance", note: "Advancing equitable cyberspace rules through diplomacy and global cooperation.", image: "https://alearninglab.com/wp-content/uploads/2025/09/Chan-Leem.png" },
    { id: "andrew-couch", name: "Andrew Couch", role: "PhD Candidate", org: "Stanford University", note: "Data-driven decision and risk analysis for economic resilience.", image: "https://alearninglab.com/wp-content/uploads/2025/09/Andrew-Couch.png" },
    { id: "sang-truong", name: "Sang Truong", role: "PhD Researcher", org: "Stanford University", note: "Aligning AI systems with human values and decision theory.", image: "https://alearninglab.com/wp-content/uploads/2025/09/Frame-1707483342.png" },
    { id: "jocelyn-a-ricard", name: "Jocelyn A. Ricard", role: "PhD Neuroscientist", org: "Stanford University", note: "Researching adolescent brain development and equitable neuroscience.", image: "https://alearninglab.com/wp-content/uploads/2025/09/Jocelyn-Richard.png" },
    { id: "gabriel-khoury", name: "Gabriel Khoury", role: "Learning Experience Mentor", org: "Harvard College (Cum Laude)", note: "Designing rigorous, high-impact learning experiences for ambitious students.", image: "https://alearninglab.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-10-at-15.01.58.jpeg" },
    { id: "shokhrukhbek", name: "Shokhrukhbek", role: "International Economics / MBA", org: "Georgetown University Qatar & NYU Stern", note: "Finance and technology specialist bridging strategy and academics.", image: "https://alearninglab.com/wp-content/uploads/2026/03/Screen-Shot-2026-03-14-at-5.39.39-PM.png" },
    { id: "ana-mara-leppink", name: "Ana-Mara Leppink", role: "Applied Math & Environmental Policy", org: "Harvard", note: "Bridging sustainability, math, and youth leadership.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Ana-Mara-Leppink-1.jpg" },
    { id: "ashley-redhead", name: "Ashley Redhead", role: "Electrical Engineering", org: "Harvard College", note: "AI and consumer electronics innovator with vision.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Ashley-Redhead-2.jpg" },
    { id: "calvin-reddick-jr", name: "Calvin Reddick Jr.", role: "Doctoral Student", org: "Harvard Graduate School of Education", note: "25 years leading diverse school communities.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Calvin-Reddick-Jr.jpg" },
    { id: "daniel-mcquade", name: "Daniel McQuade", role: "Former Columbia Business School Instructor", org: "", note: "Serial entrepreneur with 30 years in marketing.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Daniel-McQuade.jpg" },
    { id: "daniela-solis", name: "Daniela Solis", role: "Government", org: "Harvard College", note: "Building AI tools for educational access.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Daniela-Solis.jpg" },
    { id: "erin-buisse", name: "Erin Buisse", role: "Founder, Erin Buisse Consulting", org: "PhD, Harvard", note: "15+ years in global inclusive education strategy.", image: "https://alearninglab.com/wp-content/uploads/2026/06/erin-hayba-buisse.jpg" },
    { id: "jessica-zhuo", name: "Jessica Zhuo", role: "Neuroscience", org: "Harvard College", note: "Bridging health equity through social entrepreneurship.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Jessica-Zhuo.jpg" },
    { id: "jessie-wang", name: "Jessie Wang", role: "Economics & Psychology", org: "Harvard College", note: "Public service advocate, empowering communities.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Jessie-Wang-1.jpg" },
    { id: "jonada-shukarasi", name: "Jonada Shukarasi", role: "Government", org: "Harvard College", note: "Women's rights advocate focused on Eastern Europe.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Jonada-Shukarasi.jpg" },
    { id: "kassandra-rodriguez-acosta", name: "Kassandra Rodriguez-Acosta", role: "Computer Science & Philosophy", org: "Harvard", note: "First-gen student championing immigrant and higher education.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Kassandra-Rodriguez-Acosta.jpg" },
    { id: "khari-austin-rawls", name: "Khari Austin-Rawls", role: "Harvard Business School (Class of 2025)", org: "", note: "Disney strategist turned AI startup Chief of Staff.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Khari.jpg" },
    { id: "mateo-velarde", name: "Mateo Velarde", role: "CS, Government & Theatre", org: "Harvard", note: "Guiding students through college and leadership.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Mateo-Velarde.jpg" },
    { id: "namira-mehedi", name: "Namira Mehedi", role: "Chemistry", org: "Harvard College", note: "Mentoring through social and leadership skills.", image: "https://alearninglab.com/wp-content/uploads/2026/06/Namira-Mehedi.jpg" },
  ],
};

export const STORIES = {
  title: "The stories behind the experience",
  reviewsTitle: "The experience speaks for itself",
  // SAMPLE DATA: replace with real video testimonials (YouTube ids) and Google reviews.
  sample: true,
  videos: [
    // Placeholder YouTube ids only — swap for real testimonial embed ids before launch.
    { id: "v1", name: "Student name", line: "Why I tried a career before choosing one.", youtubeId: "aqz-KE-bpKQ" },
    { id: "v2", name: "Student name", line: "What I built in four weeks.", youtubeId: "LXb3EKWsInQ" },
    { id: "v3", name: "Parent name", line: "What changed for my daughter.", youtubeId: "tgbNymZ7vqY" },
  ],
  reviews: [
    { id: "r1", name: "Reviewer name", stars: 5, text: "Review text goes here once the Google reviews feed is connected." },
    { id: "r2", name: "Reviewer name", stars: 5, text: "Review text goes here once the Google reviews feed is connected." },
    { id: "r3", name: "Reviewer name", stars: 5, text: "Review text goes here once the Google reviews feed is connected." },
  ],
};

export const BEYOND = {
  title: "Go beyond what you know",
  intro: "Anyone can collect activities. What counts is what you take away.",
  items: [
    { id: "doing", title: "Learning by doing", text: "Research, build, present, solve." },
    { id: "beyond-title", title: "Beyond a job title", text: "See an industry up close before you choose it." },
    {
      id: "people",
      title: "People & perspectives",
      text: "Meet new people, learn from them, & build connections along the way.",
    },
    { id: "outputs", title: "Outputs that are yours", text: "Projects, portfolios, research. No exams, just proof." },
    { id: "space", title: "Space to discover", text: "Find what you love. Or cross off what you don't." },
    { id: "skills", title: "Skills that stick", text: "Communication, teamwork, critical thinking, confidence." },
  ],
  seci: {
    title: "How every experience is designed",
    intro: "A learning cycle designed to take you from curiosity to confidence.",
    loop: "Then go round again, a little braver each time.",
    steps: [
      { id: "s", letter: "S", title: "Socialization", text: "Learn from peers, professionals and mentors." },
      { id: "e", letter: "E", title: "Externalization", text: "Turn curiosity into action with live tasks and projects." },
      { id: "c", letter: "C", title: "Combination", text: "Connect classroom learning with industry insight." },
      { id: "i", letter: "I", title: "Internalization", text: "Reflect, and work out who you are and what you want." },
    ],
  },
};

export const FINAL_CTA = {
  title: "What will you explore",
  accent: "next?",
  sub: "Tell us what you're curious about. We'll help you find the experience that fits.",
  cta: { label: "Book a free chat", to: "/consultation" },
  secondary: { label: "Get in touch", to: "/contact" },
  stepsTitle: "Here's how it starts",
  steps: [
    { id: "tell", title: "Tell us what you like", text: "Subjects, hobbies, big questions. Anything goes." },
    { id: "chat", title: "Have a quick chat", text: "We'll talk through experiences that match you." },
    { id: "pick", title: "Pick your experience", text: "Choose a programme and a time that works." },
  ],
};
